import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Users, UserPlus, Check, X, Trash2, Shield, Eye, EyeOff, KeyRound, Crown, Lock, Search, GitMerge, AlertCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { MergeUsersModal } from '../../components/admin/MergeUsersModal';
import { PhoneInput } from '../../components/PhoneInput';
import { RecoveryRequestsPanel, TempPasswordResult, useTempPassword } from '../../components/admin/RecoveryRequests';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_OPTIONS = [
  { value: '', label: 'Auto (par quantité)' },
  { value: 'semi_gros', label: 'Semi-gros forcé' },
  { value: 'gros', label: 'Gros forcé' },
  { value: 'vip', label: 'VIP forcé' },
];

const TIER_BADGE = {
  semi_gros: { label: 'Semi-gros', cls: 'text-blue-300 border-blue-500/40 bg-blue-500/5' },
  gros: { label: 'Gros', cls: 'text-emerald-300 border-emerald-500/40 bg-emerald-500/5' },
  vip: { label: 'VIP', cls: 'text-[color:var(--color-primary)] border-[color:rgb(var(--primary-rgb)/0.50)] bg-[color:rgb(var(--primary-rgb)/0.05)]' },
};

// Phase 38 — permission catalog shown in the AdminUsers editor.
// Order = visual order in the UI; key = backend identifier.
const PERMISSION_CATALOG = [
  { key: 'products',          label: 'Produits',                hint: 'Catalogue & stocks' },
  { key: 'orders',            label: 'Commandes',               hint: 'Voir & préparer les commandes web' },
  { key: 'pos',               label: 'POS / Comptoir',          hint: 'Ventes au comptoir' },
  { key: 'users',             label: 'Utilisateurs',            hint: 'Gérer les comptes clients' },
  { key: 'featured',          label: 'Coups de cœur',           hint: 'Sélection homepage' },
  { key: 'promo',             label: 'Codes promo',             hint: 'Créer / gérer' },
  { key: 'delivery_notes',    label: 'Bons de livraison',       hint: 'Édition des bons' },
  { key: 'analytics',         label: 'Statistiques',            hint: 'CA, marges, KPI' },
  { key: 'credit_customers',  label: 'Dettes clients',          hint: 'Comptabilité — clients' },
  { key: 'credit_suppliers',  label: 'Dettes fournisseurs',     hint: 'Comptabilité — fournisseurs' },
  { key: 'treasury',          label: 'Trésorerie',              hint: '⚠️ Sensible — Oussama / Rachid' },
];

const AdminUsers = () => {
  const { isAdmin, hasPermission } = useAuth();
  const canAccess = hasPermission('users');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  // Honour ?filter=pending coming from the header notification bell so the
  // admin lands directly on the users awaiting approval.
  const initialFilter = ['pending', 'approved', 'rejected', 'employee', 'admin', 'all'].includes(
    searchParams.get('filter')
  ) ? searchParams.get('filter') : 'all';
  const [filter, setFilter] = useState(initialFilter);
  const [search, setSearch] = useState('');
  const [mergeTarget, setMergeTarget] = useState(null); // user object being kept
  const [duplicateIds, setDuplicateIds] = useState(new Set());
  const [duplicateGroups, setDuplicateGroups] = useState(0);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newEmployee, setNewEmployee] = useState({ name: '', phone: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [creating, setCreating] = useState(false);
  const [resetUserId, setResetUserId] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const tempPwd = useTempPassword();
  const [permsUserId, setPermsUserId] = useState(null);
  const [draftPerms, setDraftPerms] = useState([]);

  const openPermissionsEditor = (u) => {
    setPermsUserId(u.id);
    setDraftPerms(Array.isArray(u.permissions) ? [...u.permissions] : []);
  };

  const togglePerm = (key) => {
    setDraftPerms((prev) =>
      prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key]
    );
  };

  const savePermissions = async () => {
    try {
      const res = await axios.put(
        `${API_URL}/api/admin/users/${permsUserId}/permissions`,
        { permissions: draftPerms },
      );
      setUsers((prev) => prev.map((u) =>
        u.id === permsUserId ? { ...u, permissions: res.data.permissions } : u
      ));
      toast.success('Permissions mises à jour');
      setPermsUserId(null);
    } catch (e) {
      toast.error('Échec de la mise à jour');
    }
  };

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/users`);
      setUsers(response.data);
    } catch (error) {
      // Error shown via loading=false + null data check in UI
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDuplicates = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/users/duplicates`);
      const ids = new Set();
      (res.data?.groups || []).forEach((g) => g.users.forEach((u) => ids.add(u.id)));
      setDuplicateIds(ids);
      setDuplicateGroups(res.data?.count || 0);
    } catch {
      // silent
    }
  }, []);

  useEffect(() => {
    if (!canAccess) {
      navigate('/connexion');
      return;
    }
    fetchUsers();
    fetchDuplicates();
  }, [canAccess, navigate, fetchUsers, fetchDuplicates]);

  const handleMerged = useCallback(() => {
    fetchUsers();
    fetchDuplicates();
  }, [fetchUsers, fetchDuplicates]);

  const approveUser = async (userId) => {
    try {
      await axios.put(`${API_URL}/api/admin/users/${userId}/approve`, {});
      setUsers(users.map(u => u.id === userId ? { ...u, status: 'approved' } : u));
      toast.success('Compte approuvé');
    } catch (error) {
      toast.error('Erreur lors de l\'approbation');
    }
  };

  const rejectUser = async (userId) => {
    try {
      await axios.put(`${API_URL}/api/admin/users/${userId}/reject`, {});
      setUsers(users.map(u => u.id === userId ? { ...u, status: 'rejected' } : u));
      toast.success('Compte refusé');
    } catch (error) {
      toast.error('Erreur lors du refus');
    }
  };

  const setPricingTier = async (userId, tier) => {
    try {
      const payload = { tier: tier || null };
      const res = await axios.put(
        `${API_URL}/api/admin/users/${userId}/pricing-tier`, payload
      );
      setUsers(users.map(u => u.id === userId
        ? { ...u, pricing_tier: res.data.pricing_tier ?? null }
        : u));
      toast.success(tier
        ? `Tarif ${TIER_BADGE[tier]?.label || tier} appliqué`
        : 'Tarif remis en automatique');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du changement de tarif');
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Supprimer cet utilisateur ?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/users/${userId}`);
      setUsers(users.filter(u => u.id !== userId));
      toast.success('Utilisateur supprimé');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleResetPassword = async (userId) => {
    if (!resetPassword || resetPassword.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    try {
      await axios.put(
        `${API_URL}/api/admin/users/${userId}/reset-password`,
        { new_password: resetPassword },
        
      );
      toast.success('Mot de passe réinitialisé');
      setResetUserId(null);
      setResetPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la réinitialisation');
    }
  };

  const createEmployee = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const response = await axios.post(`${API_URL}/api/admin/employees`, newEmployee);
      setUsers([response.data, ...users]);
      setNewEmployee({ name: '', phone: '', password: '' });
      setShowCreateForm(false);
      toast.success('Employé créé avec succès');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const getRoleLabel = (role) => {
    const labels = { admin: 'Admin', employee: 'Employé', customer: 'Client' };
    return labels[role] || role;
  };

  const getRoleColor = (role) => {
    const colors = {
      admin: 'text-[color:var(--color-primary)] bg-[color:rgb(var(--primary-rgb)/0.10)]',
      employee: 'text-blue-400 bg-blue-400/10',
      customer: 'text-zinc-400 bg-zinc-400/10'
    };
    return colors[role] || 'text-zinc-400 bg-zinc-400/10';
  };

  const getStatusLabel = (status) => {
    const labels = { approved: 'Approuvé', pending: 'En attente', rejected: 'Refusé' };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      approved: 'text-green-500 bg-green-500/10',
      pending: 'text-orange-400 bg-orange-400/10',
      rejected: 'text-red-500 bg-red-500/10'
    };
    return colors[status] || 'text-zinc-400 bg-zinc-400/10';
  };

  const filters = ['all', 'pending', 'duplicates', 'employee', 'customer'];
  const filteredUsers = useMemo(() => {
    let arr = users;
    if (filter === 'pending') arr = arr.filter((u) => u.status === 'pending');
    else if (filter === 'employee' || filter === 'customer') arr = arr.filter((u) => u.role === filter);
    else if (filter === 'duplicates') arr = arr.filter((u) => duplicateIds.has(u.id));
    // "all" → no filter
    const q = search.trim().toLowerCase();
    if (q) {
      arr = arr.filter((u) =>
        (u.name || '').toLowerCase().includes(q) ||
        (u.phone || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q)
      );
    }
    return arr;
  }, [users, filter, duplicateIds, search]);

  if (!canAccess) return null;

  return (
    <div className="min-h-screen bg-[color:var(--color-bg)] pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <Link to="/admin" className="text-zinc-500 hover:text-white text-sm mb-2 inline-block">
              ← Retour au dashboard
            </Link>
            <h1 
              className="text-3xl text-white font-light"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Gestion des Utilisateurs
            </h1>
          </div>
          {isAdmin && (
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="flex items-center gap-2 bg-[color:var(--color-primary)] text-black px-4 py-2 hover:bg-[color:var(--color-primary-hover)] transition-colors text-sm"
            data-testid="create-employee-btn"
          >
            <UserPlus className="w-4 h-4" />
            Créer un Employé
          </button>
          )}
        </div>

        <RecoveryRequestsPanel onResolved={fetchUsers} />

        {/* Create Employee Form */}
        {showCreateForm && (
          <div className="bg-[color:var(--color-surface)] border border-[color:rgb(var(--primary-rgb)/0.30)] p-6 mb-6" data-testid="create-employee-form">
            <h3 className="text-white mb-4 flex items-center gap-2">
              <Shield className="w-4 h-4 text-[color:var(--color-primary)]" />
              Nouveau compte employé
            </h3>
            <form onSubmit={createEmployee} className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <input
                type="text"
                value={newEmployee.name}
                onChange={(e) => setNewEmployee({ ...newEmployee, name: e.target.value })}
                placeholder="Nom complet"
                required
                className="bg-transparent border border-white/10 focus:border-[color:var(--color-primary)] text-white px-4 py-3 outline-none"
                data-testid="employee-name-input"
              />
              <PhoneInput value={newEmployee.phone} onChange={(v) => setNewEmployee(prev => ({ ...prev, phone: v }))} testId="employee-phone-input" />
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={newEmployee.password}
                  onChange={(e) => setNewEmployee({ ...newEmployee, password: e.target.value })}
                  placeholder="Mot de passe"
                  required
                  minLength={6}
                  className="w-full bg-transparent border border-white/10 focus:border-[color:var(--color-primary)] text-white px-4 py-3 pr-12 outline-none"
                  data-testid="employee-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={creating}
                  className="flex-1 bg-[color:var(--color-primary)] text-black px-4 py-3 hover:bg-[color:var(--color-primary-hover)] transition-colors disabled:opacity-50 text-sm"
                  data-testid="employee-submit-btn"
                >
                  {creating ? 'Création...' : 'Créer'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="px-4 py-3 border border-white/10 text-zinc-400 hover:text-white transition-colors text-sm"
                >
                  Annuler
                </button>
              </div>
            </form>
            <p className="text-zinc-600 text-xs mt-3">
              Les employés ont accès au dashboard mais ne voient pas les prix, revenus, détails clients ni les codes promo.
            </p>
          </div>
        )}

        {/* Search bar */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un utilisateur (nom, téléphone, email)…"
            className="w-full pl-9 pr-9 py-3 bg-transparent border border-white/10 focus:border-[color:var(--color-primary)] text-white outline-none"
            data-testid="user-search-input"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
              aria-label="Effacer la recherche"
              data-testid="user-search-clear"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          {filters.map(f => {
            const label = {
              all: 'Tous',
              pending: 'En attente',
              duplicates: 'Doublons possibles',
              employee: 'Employés',
              customer: 'Clients',
            }[f];
            const pendingCount = users.filter(u => u.status === 'pending').length;
            return (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-4 py-2 text-sm uppercase tracking-wider transition-colors ${
                  filter === f
                    ? 'bg-[color:var(--color-primary)] text-black'
                    : 'border border-white/10 text-zinc-400 hover:border-white/20'
                }`}
                data-testid={`filter-${f}`}
              >
                {label}
                {f === 'pending' && pendingCount > 0 && (
                  <span className="ml-2 bg-orange-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                    {pendingCount}
                  </span>
                )}
                {f === 'duplicates' && duplicateGroups > 0 && (
                  <span className="ml-2 bg-amber-500 text-black text-xs px-1.5 py-0.5 rounded-full" data-testid="duplicates-count-badge">
                    {duplicateGroups}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Users List */}
        {loading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={`skeleton-${i}`} className="bg-[color:var(--color-surface)] border border-white/10 h-20 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="bg-[color:var(--color-surface)] border border-white/10 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left p-4 text-zinc-500 text-xs uppercase tracking-wider">Utilisateur</th>
                    <th className="text-left p-4 text-zinc-500 text-xs uppercase tracking-wider">Rôle</th>
                    <th className="text-left p-4 text-zinc-500 text-xs uppercase tracking-wider">Statut</th>
                    <th className="text-left p-4 text-zinc-500 text-xs uppercase tracking-wider">Tarif privilégié</th>
                    <th className="text-left p-4 text-zinc-500 text-xs uppercase tracking-wider">Inscrit le</th>
                    <th className="text-right p-4 text-zinc-500 text-xs uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody data-testid="users-table">
                  {filteredUsers.map(u => (
                    <tr 
                      key={u.id} 
                      className="border-b border-white/5 hover:bg-white/5 transition-colors"
                      data-testid={`user-row-${u.id}`}
                    >
                      <td className="p-4">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-white">{u.name}</p>
                          {duplicateIds.has(u.id) && (
                            <span
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-amber-300 border border-amber-500/40 bg-amber-500/10"
                              title="Un ou plusieurs comptes similaires ont été détectés — envisagez une fusion"
                              data-testid={`duplicate-badge-${u.id}`}
                            >
                              <AlertCircle className="w-3 h-3" />
                              Doublon possible
                            </span>
                          )}
                          {u.source === 'pos_walkin' && (
                            <span
                              className="inline-flex px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-cyan-300 border border-cyan-500/30 bg-cyan-500/5"
                              title="Compte créé automatiquement lors d'une vente au comptoir"
                              data-testid={`walkin-badge-${u.id}`}
                            >
                              Comptoir
                            </span>
                          )}
                          {!u.has_password && (
                            <span
                              className="inline-flex px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-zinc-400 border border-white/10"
                              title="Aucun mot de passe défini — le client ne peut pas encore se connecter"
                            >
                              Sans mdp
                            </span>
                          )}
                        </div>
                        <p className="text-zinc-500 text-sm">{u.phone}</p>
                        {u.email && <p className="text-zinc-600 text-xs">{u.email}</p>}
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 text-xs uppercase tracking-wider ${getRoleColor(u.role)}`}>
                          {getRoleLabel(u.role)}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 text-xs uppercase tracking-wider ${getStatusColor(u.status)}`}>
                          {getStatusLabel(u.status)}
                        </span>
                      </td>
                      <td className="p-4">
                        {u.role === 'admin' || u.role === 'employee' ? (
                          <span className="text-zinc-600 text-xs">—</span>
                        ) : (
                          <div className="flex items-center gap-2">
                            <select
                              value={u.pricing_tier || ''}
                              onChange={(e) => setPricingTier(u.id, e.target.value)}
                              data-testid={`tier-select-${u.id}`}
                              className="bg-black border border-white/10 text-white text-xs px-2 py-1 focus:border-[color:var(--color-primary)] outline-none"
                            >
                              {TIER_OPTIONS.map(o => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </select>
                            {u.pricing_tier && TIER_BADGE[u.pricing_tier] && (
                              <span
                                className={`flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider border ${TIER_BADGE[u.pricing_tier].cls}`}
                                data-testid={`tier-badge-${u.id}`}
                              >
                                {u.pricing_tier === 'vip' && <Crown className="w-3 h-3" />}
                                {TIER_BADGE[u.pricing_tier].label}
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="p-4 text-zinc-400 text-sm">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString('fr-FR') : '-'}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-2">
                          {u.status === 'pending' && (
                            <>
                              <button
                                onClick={() => approveUser(u.id)}
                                className="p-2 hover:bg-green-500/10 transition-colors"
                                title="Approuver"
                                data-testid={`approve-user-${u.id}`}
                              >
                                <Check className="w-4 h-4 text-green-500" />
                              </button>
                              <button
                                onClick={() => rejectUser(u.id)}
                                className="p-2 hover:bg-red-500/10 transition-colors"
                                title="Refuser"
                                data-testid={`reject-user-${u.id}`}
                              >
                                <X className="w-4 h-4 text-red-500" />
                              </button>
                            </>
                          )}
                          {u.role !== 'admin' && (
                            <>
                              {isAdmin && u.role === 'employee' && (
                                <button
                                  onClick={() => openPermissionsEditor(u)}
                                  className="p-2 hover:bg-cyan-500/10 transition-colors"
                                  title="Modifier les permissions"
                                  data-testid={`edit-perms-${u.id}`}
                                >
                                  <Lock className="w-4 h-4 text-cyan-400" />
                                </button>
                              )}
                              <button
                                onClick={() => {
                                  setResetUserId(resetUserId === u.id ? null : u.id);
                                  setResetPassword('');
                                }}
                                className="p-2 hover:bg-[color:rgb(var(--primary-rgb)/0.10)] transition-colors"
                                title="Réinitialiser le mot de passe"
                                data-testid={`reset-pwd-${u.id}`}
                              >
                                <KeyRound className="w-4 h-4 text-[color:var(--color-primary)]" />
                              </button>
                              <button
                                onClick={() => deleteUser(u.id)}
                                className="p-2 hover:bg-white/10 transition-colors"
                                title="Supprimer"
                                data-testid={`delete-user-${u.id}`}
                              >
                                <Trash2 className="w-4 h-4 text-zinc-400 hover:text-red-500" />
                              </button>
                              {u.role === 'customer' && (
                                <button
                                  onClick={() => setMergeTarget(u)}
                                  className="p-2 hover:bg-white/10 transition-colors"
                                  title="Fusionner un autre client dans celui-ci"
                                  data-testid={`merge-user-${u.id}`}
                                >
                                  <GitMerge className="w-4 h-4 text-zinc-400 hover:text-[color:var(--color-primary)]" />
                                </button>
                              )}
                            </>
                          )}
                        </div>
                        {resetUserId === u.id && (
                          <div className="flex items-center gap-2 mt-2" data-testid={`reset-form-${u.id}`}>
                            <input
                              type="text"
                              value={resetPassword}
                              onChange={(e) => setResetPassword(e.target.value)}
                              placeholder="Nouveau mot de passe"
                              className="bg-transparent border border-white/10 focus:border-[color:var(--color-primary)] text-white text-sm px-3 py-1.5 outline-none w-40"
                              data-testid={`reset-input-${u.id}`}
                            />
                            <button
                              onClick={() => handleResetPassword(u.id)}
                              className="px-3 py-1.5 bg-[color:var(--color-primary)] text-black text-xs hover:bg-[color:var(--color-primary-hover)] transition-colors"
                              data-testid={`reset-confirm-${u.id}`}
                            >
                              Valider
                            </button>
                            <button
                              onClick={() => { setResetUserId(null); setResetPassword(''); }}
                              className="px-3 py-1.5 border border-white/10 text-zinc-400 text-xs hover:text-white transition-colors"
                            >
                              Annuler
                            </button>
                            <button
                              type="button"
                              onClick={() => { setResetUserId(null); setResetPassword(''); tempPwd.generate(`${API_URL}/api/admin/users/${u.id}/temp-password`); }}
                              className="px-3 py-1.5 border border-[color:rgb(37_211_102/0.50)] text-[#25D366] text-xs hover:bg-[#25D366] hover:text-black transition-colors"
                              title="Générer un mot de passe temporaire et l'envoyer par WhatsApp"
                              data-testid={`temp-password-${u.id}`}
                            >
                              MDP temporaire → WhatsApp
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredUsers.length === 0 && (
              <div className="text-center py-12">
                <Users className="w-8 h-8 text-zinc-600 mx-auto mb-3" />
                <p className="text-zinc-500">Aucun utilisateur trouvé</p>
              </div>
            )}
          </div>
        )}

        {/* Merge users modal */}
        {tempPwd.result && <TempPasswordResult result={tempPwd.result} onClose={tempPwd.clear} />}
        {mergeTarget && (
          <MergeUsersModal
            target={mergeTarget}
            allUsers={users}
            onClose={() => setMergeTarget(null)}
            onMerged={handleMerged}
          />
        )}

        {/* Permissions editor modal (Phase 38) */}
        {permsUserId && (() => {
          const target = users.find((u) => u.id === permsUserId);
          return (
            <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
                 data-testid="perms-modal">
              <div className="bg-[color:var(--color-surface)] border border-[color:rgb(var(--primary-rgb)/0.30)] max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-xl text-white font-light"
                        style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                      Permissions employé
                    </h2>
                    <p className="text-zinc-500 text-xs">{target?.name} · {target?.phone}</p>
                  </div>
                  <button type="button"
                    onClick={() => setPermsUserId(null)}
                    data-testid="perms-close"
                    className="text-zinc-500 hover:text-white p-1">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <p className="text-zinc-500 text-xs mb-4">
                  Cochez les sections auxquelles cet employé peut accéder. L'admin a toujours tous les accès.
                </p>

                <div className="space-y-2 mb-5">
                  {PERMISSION_CATALOG.map((p) => {
                    const checked = draftPerms.includes(p.key);
                    return (
                      <label
                        key={p.key}
                        className={`flex items-start gap-3 border p-3 cursor-pointer transition-colors ${
                          checked
                            ? 'border-[color:rgb(var(--primary-rgb)/0.40)] bg-[color:rgb(var(--primary-rgb)/0.05)]'
                            : 'border-white/10 hover:border-white/20'
                        }`}
                        data-testid={`perm-${p.key}`}
                      >
                        <input type="checkbox"
                          checked={checked}
                          onChange={() => togglePerm(p.key)}
                          className="mt-1 accent-[color:var(--color-primary)]"
                          data-testid={`perm-checkbox-${p.key}`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm">{p.label}</p>
                          <p className="text-zinc-500 text-xs">{p.hint}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>

                <div className="flex gap-2">
                  <button type="button" onClick={savePermissions}
                    data-testid="perms-save"
                    className="flex-1 bg-[color:var(--color-primary)] text-black py-2.5 text-xs uppercase tracking-wider hover:bg-[color:var(--color-primary-hover)]">
                    Enregistrer ({draftPerms.length})
                  </button>
                  <button type="button"
                    onClick={() => setPermsUserId(null)}
                    className="border border-white/20 text-zinc-400 px-5 py-2.5 text-xs uppercase tracking-wider hover:border-white/40">
                    Annuler
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default AdminUsers;
